/**
 * Pro access given by hand, without payment: one record per address at `pro_grants/<email>`,
 * written by whoever runs the site (an address listed in `admins`, which is written in the
 * Firebase console only), read by that person and by admins. The app treats an active grant
 * as the Pro plan wherever it looks at the plan (utils/subscriptionAccess.ts
 * getEffectiveTier).
 *
 *   pro_grants/{email in lower case}: { email, tier: 'pro' | 'enterprise', expiresAt: number | null, note?, grantedBy, grantedAt }
 *   admins/{email in lower case}:     { } (its presence is what counts)
 */
import { useEffect, useState } from 'react';
import { collection, deleteDoc, doc, getDoc, getFirestore, onSnapshot, orderBy, query, setDoc } from 'firebase/firestore';
import { describeCloudError, getFirebaseApp, isFirebaseActive, withCloudTimeout } from './storage';

export interface ProGrant {
    tier: 'pro' | 'enterprise';
    /** When it ends, in ms; null for as long as the record is there */
    expiresAt: number | null;
    note?: string;
}

/** A grant as the admin screen lists it */
export interface ProGrantRecord extends ProGrant {
    email: string;
    grantedBy: string;
    grantedAt: number;
}

export const grantIsActive = (grant: ProGrant | null | undefined, now: number = Date.now()): grant is ProGrant =>
    !!grant && (grant.expiresAt === null || grant.expiresAt > now);

export const normalizeAddress = (email: string): string => email.trim().toLowerCase();
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const db = () => {
    const app = getFirebaseApp();
    if (!app || !isFirebaseActive()) throw new Error('The cloud is not there.');
    return getFirestore(app);
};

const toGrant = (data: Record<string, any> | undefined): ProGrant | null => {
    if (!data) return null;
    const tier = data.tier === 'enterprise' ? 'enterprise' : 'pro';
    const expiresAt = typeof data.expiresAt === 'number' ? data.expiresAt : (data.expiresAt && typeof data.expiresAt.toMillis === 'function' ? data.expiresAt.toMillis() : null);
    return { tier, expiresAt, ...(typeof data.note === 'string' && data.note ? { note: data.note } : {}) };
};

/** The grant for an address, as it changes; null when there is none or it cannot be read. */
export const watchProGrant = (email: string, onGrant: (grant: ProGrant | null) => void): (() => void) => {
    const app = getFirebaseApp();
    const address = normalizeAddress(email);
    if (!app || !isFirebaseActive() || !address) { onGrant(null); return () => {}; }
    try {
        return onSnapshot(
            doc(getFirestore(app), 'pro_grants', address),
            (snapshot) => onGrant(snapshot.exists() ? toGrant(snapshot.data() as Record<string, any>) : null),
            (error) => { console.warn('[plan] the grant could not be read:', error?.code || error); onGrant(null); },
        );
    } catch (e) {
        console.warn('[plan] the grant is not watched:', e);
        onGrant(null);
        return () => {};
    }
};

/** The signed-in person's grant, kept current. */
export const useProGrant = (email: string | null | undefined): ProGrant | null => {
    const [grant, setGrant] = useState<ProGrant | null>(null);
    useEffect(() => {
        if (!email) { setGrant(null); return; }
        return watchProGrant(email, setGrant);
    }, [email]);
    return grant;
};

// ── Whoever runs the site ────────────────────────────────────────────────────

/** Is this address listed among the site's admins? (one read; false when it cannot be read) */
export const isAdminAddress = async (email: string): Promise<boolean> => {
    const address = normalizeAddress(email);
    const app = getFirebaseApp();
    if (!app || !isFirebaseActive() || !address) return false;
    try {
        return (await getDoc(doc(getFirestore(app), 'admins', address))).exists();
    } catch (e) {
        console.warn('[admin] not checked:', (e as any)?.code || e);
        return false;
    }
};

/** Whether the signed-in person runs the site; false until known. */
export const useIsAdmin = (email: string | null | undefined): boolean => {
    const [admin, setAdmin] = useState(false);
    useEffect(() => {
        let gone = false;
        setAdmin(false);
        if (!email) return;
        isAdminAddress(email).then(result => { if (!gone) setAdmin(result); });
        return () => { gone = true; };
    }, [email]);
    return admin;
};

const toRecord = (id: string, data: Record<string, any>): ProGrantRecord => ({
    ...(toGrant(data) ?? { tier: 'pro', expiresAt: null }),
    email: typeof data.email === 'string' && data.email ? data.email : id,
    grantedBy: typeof data.grantedBy === 'string' ? data.grantedBy : '',
    grantedAt: typeof data.grantedAt === 'number' ? data.grantedAt : 0,
});

/** Every grant, newest first, as they change (admins only). */
export const watchProGrants = (onGrants: (grants: ProGrantRecord[]) => void, onError: (message: string) => void): (() => void) =>
    onSnapshot(
        query(collection(db(), 'pro_grants'), orderBy('grantedAt', 'desc')),
        (snapshot) => onGrants(snapshot.docs.map(d => toRecord(d.id, d.data() as Record<string, any>))),
        (error) => { console.warn('[admin] grants not read:', error?.code || error); onError(describeCloudError(error, 'The grants could not be read')); },
    );

/** Give Pro to an address (admins only). A grant to an address that has one replaces it. */
export const grantPro = async (by: { id: string; email: string }, email: string, grant: { tier?: 'pro' | 'enterprise'; expiresAt: number | null; note?: string }): Promise<ProGrantRecord> => {
    const address = normalizeAddress(email);
    if (!EMAIL.test(address)) throw new Error('That does not look like an email address.');
    if (grant.expiresAt !== null && grant.expiresAt <= Date.now()) throw new Error('Choose an end that is still ahead, or none.');
    const record: ProGrantRecord = {
        email: address,
        tier: grant.tier ?? 'pro',
        expiresAt: grant.expiresAt,
        ...(grant.note?.trim() ? { note: grant.note.trim().slice(0, 200) } : {}),
        grantedBy: normalizeAddress(by.email) || by.id,
        grantedAt: Date.now(),
    };
    try {
        await withCloudTimeout(() => setDoc(doc(db(), 'pro_grants', address), record));
    } catch (e) {
        throw new Error(describeCloudError(e, 'The access could not be given'));
    }
    return record;
};

/** Take a grant away (admins only). */
export const revokeGrant = async (email: string): Promise<void> => {
    try {
        await withCloudTimeout(() => deleteDoc(doc(db(), 'pro_grants', normalizeAddress(email))));
    } catch (e) {
        throw new Error(describeCloudError(e, 'The access could not be taken away'));
    }
};
